import { useState } from "react";

import { Loader2 } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { readTheaterPerformanceContext } from "@/pages/tina/items/components/panels/hitl-panel/hitl-readers";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import {
  type PlaySummary,
  useTheaterRepository,
} from "@/pages/theater/hooks/use-theater-repository";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import type { HitlResolutionInput } from "@/types/tina";

type Mode = "pick" | "create";

const MODES: ReadonlyArray<{ mode: Mode; label: string }> = [
  { mode: "pick", label: "Use an existing play" },
  { mode: "create", label: "Create a new play" },
];

export function PlayResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl } = useItemsRepository();
  const { search } = useTheaterRepository();
  const ctx = readTheaterPerformanceContext(item, hitlContext);
  const [mode, setMode] = useState<Mode>("pick");
  const [picked, setPicked] = useState<PlaySummary | undefined>();
  const [title, setTitle] = useState(ctx?.playTitle ?? "");
  const [imageUrl, setImageUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    mode === "pick"
      ? Boolean(picked?.id)
      : title.trim().length > 0 && imageUrl.trim().length > 0;

  async function handleSubmit() {
    const resolution: HitlResolutionInput =
      mode === "pick"
        ? { kind: "play.pick", play_id: picked!.id }
        : {
            kind: "play.create",
            title: title.trim(),
            image_url: imageUrl.trim(),
          };
    setSubmitting(true);
    try {
      await resolveHitl(item.id, { resolution });
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {ctx?.playTitle && (
        <p className="text-sm text-muted-foreground">
          Extracted play:{" "}
          <span className="font-medium text-foreground">{ctx.playTitle}</span>
        </p>
      )}

      <div className="flex flex-wrap gap-1">
        {MODES.map(({ mode: m, label }) => (
          <Button
            key={m}
            type="button"
            size="sm"
            variant={mode === m ? "default" : "outline"}
            onClick={() => setMode(m)}
          >
            {label}
          </Button>
        ))}
      </div>

      {mode === "pick" && (
        <RemoteSelect<PlaySummary>
          value={picked}
          triggerType="change"
          showSelected
          onValueChanged={(selected) => setPicked(selected)}
          accessorFn={(option) => ({
            id: option?.id || "",
            name: option ? option.title : "",
            iconUrl: option?.media?.photos?.[0]?.url,
          })}
          onSearch={async (query, onStatusChanged) => {
            onStatusChanged("loading");
            const results = await search(query);
            onStatusChanged("success");
            return results;
          }}
        />
      )}
      {mode === "create" && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium" htmlFor="play-create-title">
              Title
            </label>
            <Input
              id="play-create-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Play title"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium" htmlFor="play-create-image">
              Image URL
            </label>
            <Input
              id="play-create-image"
              value={imageUrl}
              onChange={(event) => setImageUrl(event.target.value)}
              placeholder="https://… image of the play poster or production still"
            />
            <span className="text-xs text-muted-foreground">
              An image is required — this HITL paused because no usable play
              image was found during extraction.
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Button
          type="button"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
        >
          {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Resolve &amp; resume
        </Button>
        <span className="text-xs text-muted-foreground">
          Re-runs play_resolution with your choice and continues to create.
        </span>
      </div>
    </div>
  );
}
